import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Flex, Box, Copy, Icon, Button, Image, WebviewWrapper } from '@elparking/components'
import FiltersBox from '../FiltersBox'
import VehicleFilterBox from '../VehicleFilterBox'
import connectUrl from 'commons/js/components/connectUrl'
import UrlParams from 'commons/js/util/UrlTo/UrlParams'
import { parseFilters, buildSearchParams, cleanFilters, hasFilterSelected } from '../../util/rentalCarFilter'
import Routes from 'commons/js/routes/routes'
import { FormattedMessage } from 'react-intl'
import FilterBoxModal from '../Modals/FilterBoxModal'
import NoResultsModal from '../Modals/NoResultsModal'
import CategoriesBoxModal from '../Modals/CategoriesBoxModal'
import FilterButton from '../FilterButton'
import HelpBox from '../HelpBox'
import Card from '../Card'
import LoaderBar, { LoadingLayer } from '../../ui/LoaderBar'
import useDebounce from 'commons/js/hooks/useDebounce'
import { STEP_1 } from '../../constants'
import { RentalCarEvents, trackTealiumEvent } from 'commons/js/services/analytics'
import scrollTo from 'commons/js/util/scrollTo'
import Phone from '../Icons/Phone'
import ClickToCallModal from '../Modals/ClickToCallModal'
import { RENTAL_CAR_CLICK_TO_CALL_RECOVER_CART } from 'commons/js/constants'
import useOnCloseModalAnalyticEvent from 'commons/js/hooks/useOnCloseModalAnalyticEvent'

const NoFilteredResults = ({ onCleanFilters }) => {
    const [showModal, setShowModal] = useState(false)

    useOnCloseModalAnalyticEvent(showModal, RentalCarEvents.showResultsView)
    useEffect(() => {
        trackTealiumEvent(RentalCarEvents.showNoResultsViews)
    }, [])
    const onClickC2CButton = () => {
        setShowModal(true)
        trackTealiumEvent(RentalCarEvents.clickC2ClinkFromNoResults)
    }
    const handleOnLoadC2CModal = () => {
        trackTealiumEvent(RentalCarEvents.showC2CModal('sin resultados filtro'))
    }
    const handleSuccessPhoneNumberCall = () => {
        trackTealiumEvent(RentalCarEvents.showC2cPhoneSentFromNoResultsWithFilters)
        trackTealiumEvent(RentalCarEvents.eventC2cPhoneSentFromNoResultsWithFilters)
    }

    return <Flex flexDirection='column' width='100%' justifyContent='center' flexWrap='wrap' alignItems='center' dataTest='no-results-container'>
        <ClickToCallModal
          isShown={showModal}
          onClose={() => setShowModal(false)}
          step='P2'
          modalType={RENTAL_CAR_CLICK_TO_CALL_RECOVER_CART}
          onShowPrivacyModal={() => setShowModal(false)}
          onLoad={handleOnLoadC2CModal}
          onSuccessPhoneNumberCallSent={handleSuccessPhoneNumberCall}
                />
        <Image src='/images/rental-car/no-results.svg' width='200px' height='200px' alignSelf='center' alt='help' />
        <Copy fontSize={['large', 'xxLarge']} align='center'><FormattedMessage defaultMessage='No hay vehículos disponibles'/></Copy>
        <Copy fontWeight='regular' align='center' padding='1em 0em'><FormattedMessage defaultMessage='En este momento no hay ningún vehículo como el que buscas. Prueba de nuevo cambiando algún filtro.'/></Copy>
        <Flex flexDirection={['column', 'row']} width='100%' justifyContent='center' flexWrap='wrap' alignItems='center' padding='1em 0em'>
            <Button width={['100%', 'fit-content']} onClick={onCleanFilters} dataTest='no-results-clear-filters-button' marginRight={['0', '1em']} marginBottom={['1em', '0em']}><FormattedMessage defaultMessage='Borrar filtros' /></Button>
            <Button.bordered width={['100%', 'fit-content']} padding='0 8px' fontSize='small' fontWeight='medium' backgroundColor='transparent' onClick={() => onClickC2CButton()}>
                <Phone color='main' height='12px' /><FormattedMessage defaultMessage='Te llamamos gratis' />
            </Button.bordered>
        </Flex>
    </Flex>
}

NoFilteredResults.propTypes = {
    onCleanFilters: PropTypes.func,
}

const Step2 = ({ search, initialFilters, setOnCloseModal, userLoaded, push, requestRates, vehicles, searchId, searchError, ratesPending, filtersConfig, goToStep, token, scrollTo, onNext, requestBuildBooking }) => {
    const [isFilterBoxOpened, setIsFilterBoxOpened] = useState(false)
    const [isCategoriesBoxOpened, setIsCategoriesOpened] = useState(false)
    const [filterBoxModalKey, setFilterBoxModalKey] = useState(new Date())
    const [categoriesBoxModalKey, setCategoriesBoxModalKey] = useState(new Date())
    const [filters, setFilters] = useState(initialFilters)
    const [refreshKey, setRefreshKey] = useState(new Date())
    const [isLoading, setLoading] = useState(false)
    const [areFirstResults, setAreFirstResults] = useState(false)
    const searchParams = new UrlParams()
    searchParams.loadFromUrl()
    searchParams.update(buildSearchParams({ search, filters }))
    const searchUrl = useDebounce(searchParams.toString(true), 1000, () => setLoading(false))
    const filterUrl = searchParams.toString(true)
    const numVehicles = (vehicles && vehicles.length) || 0
    const noResults = !ratesPending && (!vehicles || vehicles.length === 0)
    const ratesLoading = isLoading || ratesPending

    const onCleanFilters = () => {
        trackTealiumEvent(RentalCarEvents.cleanFiltersLink(!noResults))
        setRefreshKey(new Date())
        setFilters(cleanFilters(filters))
    }

    const onOpenModal = (setOpenModal, setComponentKey) => {
        setOpenModal(true)
        setComponentKey(new Date())
        setOnCloseModal(() => setOpenModal(false))
    }

    const onClickOnRate = (rate) => {
        requestBuildBooking({token, idSearch: searchId, idRate: rate.id})
        onNext()
    }

    const mainResultsEvent = areFirstResults ? RentalCarEvents.showFilteredResultsView : RentalCarEvents.showResultsView

    const mainResultsTrackView = () => {
        if (vehicles && vehicles.length) {
            trackTealiumEvent(mainResultsEvent)
            !areFirstResults && setAreFirstResults(true)
        }
    }
    const onApplyFilters = (value) => {
        setFilters(value)
    }

    const onVehicleBoxFilterChanged = (newFilters) => {
        const hasVehicleTypeChanged = newFilters.vehicleType !== filters.vehicleType
        setRefreshKey(new Date())
        setFilters({
            ...(hasVehicleTypeChanged ? cleanFilters(filters) : filters),
            vehicleType: newFilters.vehicleType,
            categoryList: newFilters.categoryList,
        })
    }

    useEffect(() => {
        mainResultsTrackView()
          // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicles])

    useEffect(() => {
        if (!ratesPending) {
            setLoading(false)
        }
    }, [ratesPending])

    useEffect(() => {
        setLoading(true)
    }, [filterUrl])

    useEffect(() => {
        scrollTo(1)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (userLoaded) {
            const searchParams = new UrlParams()
            const allParams = searchParams.loadFrom(searchUrl).getAll()
            push(Routes.rentalCarContractPath(allParams))
            requestRates({
                searchId,
                token,
                ...allParams,
            })
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [push, searchUrl, requestRates, userLoaded])

    return (
        <Box p={['0px 10px', '0px 10px', '0']}>
            <WebviewWrapper><Copy fontSize={['large', 'xxLarge']} fontColor='accent_dark' padding='0'><FormattedMessage defaultMessage='Selecciona tu vehículo'/></Copy></WebviewWrapper>
            <Flex padding={['0', '0', '2em 0em']} alignItems='flex-start'>
                <Box width='25%' display={['none', 'none', 'block']}>
                    <section role='region' aria-label='filtros de caracteristicas de vehiculos'>
                        <FiltersBox key={refreshKey} filters={filters} isLoading={ratesPending} setFilters={onApplyFilters} cleanFilters={onCleanFilters} filtersConfig={filtersConfig}/>
                        <Box mt='1em'>
                            <HelpBox onCloseAnalyticEvent={mainResultsEvent} />
                        </Box>
                    </section>
                </Box>
                <Box width={['100%', '100%', '75%']} marginLeft={['0', '0', '1em']}>
                    <Flex width='100%' flexDirection='column'>

                        <Box display={['none', 'none', 'block']} mb='10px'>
                            <section role='region' aria-label='filtro tipo de vehiculos'>
                                <VehicleFilterBox key={refreshKey} filters={filters} isLoading={ratesPending} setFilters={onVehicleBoxFilterChanged} />
                                <Box mt='10px'>
                                    <Copy as='span' padding='0' mr='0.5em' fontSize='small' fontWeight='regular'><FormattedMessage defaultMessage='Disponibles:' /></Copy>
                                    <Copy as='span' padding='0' fontSize='small' fontWeight='medium'><FormattedMessage defaultMessage='{numVehicles} vehículos' values={{ numVehicles }} /></Copy>
                                </Box>
                            </section>
                        </Box>

                        <Box display={['block', 'block', 'none']} mb='10px'>
                            <Flex width='100%' padding='1em 0' justifyContent='space-between' alignItems='center' flexDirection='row'>
                                <Box>
                                    <Copy padding='0' fontSize='small' fontWeight='regular'><FormattedMessage defaultMessage='Disponibles:' /></Copy>
                                    <Copy padding='0' fontSize='small' fontWeight='medium'><FormattedMessage defaultMessage='{numVehicles} vehículo(s)' values={{ numVehicles }} /></Copy>
                                </Box>
                                <Box>
                                    <FilterButton dataTest='filter-button-vehicles-type-modal' active iconType='car' label={<FormattedMessage defaultMessage='Categorías' />} onClick={() => onOpenModal(setIsCategoriesOpened, setCategoriesBoxModalKey)} />
                                    <FilterButton dataTest='filter-button-filters-modal' active={hasFilterSelected(filters)} iconType='filter' label={<FormattedMessage defaultMessage='Filtros' />} onClick={() => onOpenModal(setIsFilterBoxOpened, setFilterBoxModalKey)} />
                                </Box>
                            </Flex>
                            <FilterBoxModal
                              key={`filter-modal-${filterBoxModalKey}-${refreshKey}`}
                              isShown={isFilterBoxOpened}
                              onClose={() => { setOnCloseModal(null); setIsFilterBoxOpened(false) }}
                              filters={filters}
                              isLoading={ratesPending}
                              setFilters={onApplyFilters}
                              onCleanFilters={onCleanFilters}
                            />
                            <CategoriesBoxModal
                              key={`categories-modal-${categoriesBoxModalKey}`}
                              isShown={isCategoriesBoxOpened}
                              onClose={() => { setOnCloseModal(null); setIsCategoriesOpened(false) }}
                              filters={filters}
                              isLoading={ratesPending}
                              setFilters={onVehicleBoxFilterChanged}
                            />
                        </Box>
                        {searchError && <Box p='10px' backgroundColor='error10'>
                            <Copy fontColor='error_dark' padding='0' fontSize='small' textAlign='center'>
                                <Icon type='warning' fontColor='inherit' /> <FormattedMessage defaultMessage='Se ha producido un error. Inténtelo más tarde' />
                            </Copy>
                        </Box>}
                        {ratesLoading
                            ? <LoaderBar />
                            : <Box width='100%' minHeight='4px' />
                        }
                        <section role='region'>
                            {vehicles && vehicles.map((item, index) => {
                            return <Box mb='24px' key={`car_${index}`} position='relative'>
                                {ratesLoading && <LoadingLayer />}
                                <Card
                                  id={item.id}
                                  car={item.car}
                                  place={item.place}
                                  seller={item.seller}
                                  freeCancel={item.freeCancel}
                                  onClick={onClickOnRate}
                                  isMutuaUser={search.isMutuaUser}
                                  setOnCloseModal={() => {
                                    setOnCloseModal()
                                }}
                                  screenViewRollBack={() => mainResultsTrackView()}
                                />
                            </Box>
                        })}
                        </section>
                        {noResults && !!searchId && <NoFilteredResults onCleanFilters={onCleanFilters} />}
                        {noResults && !searchId && <NoResultsModal onClose={() => goToStep(STEP_1)} />}
                    </Flex>
                </Box>
            </Flex>
        </Box>
    )
}

Step2.defaultProps = {
    scrollTo,
    userLoaded: false,
    goToStep: () => {},
}

Step2.propTypes = {
    search: PropTypes.object.isRequired,
    initialFilters: PropTypes.object.isRequired,
    push: PropTypes.func.isRequired,
    requestRates: PropTypes.func,
    vehicles: PropTypes.array,
    searchId: PropTypes.any,
    searchError: PropTypes.bool,
    ratesPending: PropTypes.bool,
    filtersConfig: PropTypes.object,
    onNext: PropTypes.func,
    setOnCloseModal: PropTypes.func,
    goToStep: PropTypes.func,
    requestBuildBooking: PropTypes.func,
    scrollTo: PropTypes.func,
    token: PropTypes.string,
    userLoaded: PropTypes.bool,
}

function mapParamsToProps (_, searchParams) {
    const {
        start_date: startDate,
        end_date: endDate,
        idDestinationPOI,
        idOriginPOI,
        isMutuaUser,
        age,
    } = searchParams
    const filters = parseFilters(searchParams)

    return {
        search: {
            startDate,
            endDate,
            idDestinationPOI,
            idOriginPOI,
            isMutuaUser: isMutuaUser === 'true',
            age,
        },
        initialFilters: filters,
    }
}

export default connectUrl(mapParamsToProps)(Step2)
